import { useEffect, useState } from "react";
import { Eye, EyeOff, LockKeyhole, ShieldCheck } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import SEO from "@/components/site/SEO";
import lmsApi from "@/lib/lmsApi";

const STAFF_ROLES = ["organisation_admin", "instructor", "auditor"];

function adminDestination(user) {
  if (user.require_password_change) return "/learn/change-password";

  if (user.is_platform_admin) return "/learn/admin";

  const membership = user.memberships?.find((item) =>
    STAFF_ROLES.includes(item.role)
  );

  return membership
    ? `/learn/organisations/${membership.organisation_id}`
    : null;
}

export default function LMSAdminLogin() {
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    const check = async () => {
      try {
        const response = await lmsApi.get("/auth/me");

        if (!active) return;

        const destination = adminDestination(response.data);

        if (destination) {
          navigate(destination, { replace: true });
        }
      } catch {
        // No active session:
        // show the administration sign-in form.
      } finally {
        if (active) {
          setCheckingSession(false);
        }
      }
    };

    check();

    return () => {
      active = false;
    };
  }, [navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    setSubmitting(true);
    setError("");

    try {
      const response = await lmsApi.post("/auth/login", {
        username,
        password,
      });

      const destination = adminDestination(response.data);

      if (!destination) {
        try {
          await lmsApi.post("/auth/logout");
        } catch {
          // Login was valid but this is not
          // an administrative account.
        }

        setError(
          "This access point is restricted to authorised UKTDA and organisation administrators."
        );

        return;
      }

      navigate(destination, { replace: true });
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "We could not sign you in. Check your details and try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (checkingSession) {
    return (
      <section className="min-h-screen bg-slate-950 py-20">
        <div className="container-rail text-center text-sm text-slate-400">
          Checking secure administration session…
        </div>
      </section>
    );
  }

  return (
    <>
      <SEO
        title="Administration Login | TRAC-Learn"
        description="Secure administration access to TRAC-Learn."
        path="/learn/admin/login"
      />

      <section className="min-h-screen bg-slate-950 px-4 py-12 md:py-20">
        <div className="mx-auto max-w-md">
          <div className="mb-6 text-center">
            <p className="label-eyebrow text-slate-500">
              UK Train Driver Academy Ltd
            </p>

            <h1 className="mt-2 font-display text-3xl font-bold text-white">
              TRAC-Learn Administration
            </h1>

            <p className="mt-2 text-sm text-slate-400">
              Authorised administration and training-management access.
            </p>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl md:p-8">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-slate-700 bg-white p-2">
                <img
                  src="/TRAC-Learn.svg"
                  alt="TRAC-Learn"
                  className="max-h-full max-w-full"
                />
              </div>

              <div>
                <div className="flex items-center gap-2 text-safety">
                  <ShieldCheck className="h-4 w-4" />

                  <p className="text-xs font-bold uppercase tracking-wider">
                    Secure access
                  </p>
                </div>

                <p className="mt-1 text-sm text-slate-400">
                  Super Admin and authorised organisation staff
                </p>
              </div>
            </div>

            <form
              className="mt-7 space-y-5"
              onSubmit={handleSubmit}
            >
              <div>
                <label
                  htmlFor="admin-username"
                  className="text-sm font-semibold text-slate-200"
                >
                  Username
                </label>

                <input
                  id="admin-username"
                  name="username"
                  autoComplete="username"
                  value={username}
                  onChange={(event) =>
                    setUsername(event.target.value)
                  }
                  required
                  autoFocus
                  className="mt-2 w-full rounded-lg border border-slate-700 bg-slate-950 px-4 py-3 text-white outline-none transition focus:border-safety focus:ring-2 focus:ring-safety/20"
                />
              </div>

              <div>
                <label
                  htmlFor="admin-password"
                  className="text-sm font-semibold text-slate-200"
                >
                  Password
                </label>

                <div className="relative mt-2">
                  <input
                    id="admin-password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) =>
                      setPassword(event.target.value)
                    }
                    required
                    className="w-full rounded-lg border border-slate-700 bg-slate-950 px-4 py-3 pr-12 text-white outline-none transition focus:border-safety focus:ring-2 focus:ring-safety/20"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword((value) => !value)
                    }
                    className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-slate-500 hover:text-white"
                    aria-label={
                      showPassword
                        ? "Hide password"
                        : "Show password"
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div
                  role="alert"
                  className="rounded-lg border border-red-900/60 bg-red-950/50 px-4 py-3 text-sm text-red-200"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-safety px-5 py-3.5 font-bold text-white transition hover:bg-safety/90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <LockKeyhole className="h-5 w-5" />

                {submitting
                  ? "Signing in…"
                  : "Access administration"}
              </button>
            </form>

            <div className="mt-6 border-t border-slate-800 pt-5 text-center">
              <Link
                to="/learn/login"
                className="text-xs text-slate-500 transition hover:text-slate-300"
              >
                Learner sign in
              </Link>
            </div>
          </div>

          <p className="mt-5 text-center text-xs leading-relaxed text-slate-600">
            Administrative access is restricted and activity may be recorded
            in the TRAC-Learn audit trail.
          </p>
        </div>
      </section>
    </>
  );
}